/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, ScrollView, Text, ActivityIndicator } from 'react-native';
import i18n from 'i18n-js';
import AnimatedHeader from '@components/common/AnimatedHeader';
import LineButton from '@components/common/lineButton';
import Svg from '@styles/icons/svg';
import { scale, is24HourFormat, width } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import Button from '@styles/buttons';
import {
  formatSinceTime,
  isPhone,
  workerTypeStatus,
  workerTypeStatusNames,
  workerTypeNames,
  workerTypeStatusIconColor,
  workerTypeStatusIconName,
  isWeb,
} from '../../../../../../helpers';
import Header from '@components/settings/v2/common/SettingsHeader';

import NoConnector, { DownloadBlock } from '@components/settings/components/common/noConnector';

import { BACK_BUTTON_SETTINGS_TEXT, SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL, TEST_IDS } from '@v2/pages/common/constants/base.constants';

import { useManageConnector } from './hooks/useManageConnector';

const { connectorScreen } = TEST_IDS;

const Connector = ({ C, rootStack }) => {
  const {
    loaded,
    connectorDetails,
    hasConnector,
    machineConnector,
    renderElements,
    defaultElements,
    resetLoading,
    resetAlert,
    additionalData,
    userCompanyName,
  } = useManageConnector({ rootStack });

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const onResetConnector = () => {
    resetAlert({
      text: () => (
        <View>
          <Text style={[font.Footnote, { color: C('Label/Light/Tertiary'), textAlign: 'center' }]}>
            {i18n.t(
              'A reset will erase all of your local Connector settings and local data used by the Smart Factory Connector, but will keep all company data that is saved in the cloud'
            )}
          </Text>
          <Text style={[font.Footnote, { color: C('Label/Light/Tertiary'), textAlign: 'center', marginTop: scale(15) }]}>
            {i18n.t('After reset,')}
            <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(20), fontWeight: '700' }]}>
              {' '}
              {userCompanyName}{' '}
            </Text>
            {i18n.t('will not have any Connnector linked')}
          </Text>
          <Text style={[font.Footnote, { color: C('Label/Light/Tertiary'), textAlign: 'center', marginTop: scale(15) }]}>
            {i18n.t('After reset you can re-login your Connector to an existing company or create a new company')}
          </Text>
        </View>
      ),
    });
  };

  const checkValue = (val) => {
    return val ? val : ' - ';
  };

  const ResetBlock = () => {
    if (machineConnector.status === workerTypeStatus.Onboarding) {
      return null;
    }

    return hasConnector ? (
      <View style={{ width: '100%', paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL), marginTop: scale(18) }}>
        <View testID={connectorScreen.resetButton} style={{ width: '100%' }}>
          <LineButton
            {...{
              onPress: onResetConnector,
              text: 'Reset my Connector',
              color: 'Chart/Light/Warning',
              C,
              loading: resetLoading,
            }}
          />
        </View>
        <View style={{ paddingHorizontal: scale(15), marginTop: scale(6) }}>
          <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
            {i18n.t(
              'A reset will erase all of your local Connector settings and local data used by the Smart Factory Connector, but will keep all company data that is saved in the cloud'
            )}
          </Text>
          <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(20) }]}>
            {i18n.t('After reset you can re-login your Connector to an existing company or create a new company')}
          </Text>
        </View>
      </View>
    ) : (
      <View style={{ maxWidth: scale(330), width: isPhone ? scale(300) : scale(806), paddingBottom: scale(50) }}>
        <DownloadBlock {...{ C }} />
      </View>
    );
  };

  const ContentWrapper = (props) => {
    return (
      <View
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <HostDetails />
        <View
          style={[
            {
              flexDirection: 'row',
            },
            isPhone && { width: '100%', flexDirection: 'column' },
          ]}
        >
          {props.children}
        </View>
        <ResetBlock />
      </View>
    );
  };

  const ConnectorCard = React.memo(
    ({ item, index, itemWidth }) => {
      const w = isPhone ? scale(300) : itemWidth;
      const h = scale(112);
      return (
        <View
          style={[
            {
              width: w,
              height: h,
              marginLeft: !isPhone && index != 0 ? scale(20) : scale(0),
            },
            isPhone && {
              width: '100%',
              paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
              marginTop: scale(18),
            },
          ]}
        >
          <View
            style={[
              shadowStyles,
              {
                width: '100%',
                height: '100%',
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                backgroundColor: C('Background/Light/Field'),
              },
            ]}
          >
            <View
              style={{
                flexDirection: 'row',
                marginTop: scale(12),
                paddingHorizontal: scale(14),
              }}
            >
              <Text style={[font['Title 2'], { color: C('Label/Light/Primary') }]} numberOfLines={1}>
                {`${i18n.t(workerTypeNames[item.workerType])} ${i18n.t('Connector')}`}
              </Text>
            </View>
            <View style={{ marginTop: scale(14), paddingHorizontal: scale(14) }}>
              <Button
                {...{
                  variant: 'Dialog M',
                  type: 'Secondary',
                  onPress: item.buttonAction || (() => {}),
                  loading: item.loading,
                  borderRadius: scale(12),
                }}
              >
                {i18n.t(item.buttonText)}
              </Button>
            </View>
          </View>
        </View>
      );
    },
    (prev, next) => prev.index === next.index && prev.item === next.item
  );

  const HostDetails = () => {
    return (
      <View testID={connectorScreen.hostDetailsSection} style={{ width: '100%', paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) }}>
        <View
          style={[
            shadowStyles,
            {
              width: '100%',
              height: scale(222),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              marginTop: scale(18),
              marginBottom: scale(isPhone ? 0 : 18),
              backgroundColor: C('Background/Light/Field'),
            },
          ]}
        >
          <View
            style={{
              paddingLeft: scale(14),
              paddingVertical: scale(15),
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                height: scale(42),
              }}
            >
              <Svg
                width={scale(isWeb ? 25 : 24)}
                height={scale(isWeb ? 25 : 24)}
                color={workerTypeStatusIconColor[machineConnector.status]}
                icon={workerTypeStatusIconName[machineConnector.status]}
              />
              <View style={{ marginLeft: scale(10) }}>
                <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]}>{i18n.t(workerTypeStatus[machineConnector.status])}</Text>
              </View>
            </View>

            <View style={{ marginTop: scale(10) }}>
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>
                {`${i18n.t('Last seen')}: ${
                  machineConnector.status === workerTypeStatus.Online
                    ? i18n.t('Just now')
                    : formatSinceTime(machineConnector.lastAliveEventReceivedOn, is24HourFormat, true)
                }`}
              </Text>
            </View>

            <View style={{ marginTop: scale(10) }}>
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>
                {`${i18n.t('Version')}: ${checkValue(connectorDetails.version)}`}
              </Text>
            </View>

            <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(10) }]}>
              {`${i18n.t('Host name')}:`} {checkValue(connectorDetails.localHost)}
              {'\n'}
              {`${i18n.t('Physical address')}:`} {checkValue(connectorDetails.macAddress)}
              {'\n'}
              {`${i18n.t('Local IP address')}:`} {checkValue(connectorDetails.ipAddressLocal)}
              {'\n'}
              {`${i18n.t('Public IP address')}:`} {checkValue(connectorDetails.ipAddressV4)}
              {'\n'}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const amount = renderElements ? renderElements.length : defaultElements.length;
  const itemWidth = (width - scale(320) - (amount - 1) * scale(19)) / amount;

  if (loaded && !hasConnector) {
    return (
      <View style={{ flex: 1 }}>
        {isPhone && (
          <AnimatedHeader
            {...{
              backText: i18n.t('Settings'),
              text: i18n.t('Connector'),
              C,
              animated: false,
              back: true,
              backgroundColor: 'Background/Light/Detail',
              borderBottomWidth: 0,
            }}
          />
        )}
        <View testID={connectorScreen.noConnectorView} style={{ marginTop: scale(20), flex: 1 }}>
          <NoConnector time={machineConnector?.lastAliveEventReceivedOn} />
        </View>
      </View>
    );
  }

  return (
    <View style={{ width: '100%', height: '100%' }}>
      <Header backBtnText={BACK_BUTTON_SETTINGS_TEXT} name={'Connector'} />
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View
          style={[
            {
              alignItems: 'center',
              width: '100%',
              backgroundColor: C('Background/Light/Detail'),
            },
          ]}
        >
          {!loaded ? (
            <View style={{ width: '100%', justifyContent: 'center' }}>
              <ActivityIndicator size="small" color="#777777" />
            </View>
          ) : renderElements ? (
            <ContentWrapper>
              {renderElements?.map((item, index) => {
                return <ConnectorCard key={item.workerType} {...{ item: { ...item, ...additionalData[item.workerType] }, index, itemWidth }} />;
              })}
            </ContentWrapper>
          ) : (
            <ContentWrapper>
              {defaultElements?.map((item, index) => {
                return <ConnectorCard key={item} {...{ item: { workerType: item, status: workerTypeStatusNames.Onboarding }, index, itemWidth }} />;
              })}
            </ContentWrapper>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

export default ColorWrapper(Connector);
